import { z } from 'zod';

/**
 * Wallboards an account builds for itself (owner, 26 September 2026: "build a wall board based off
 * of a list of items and move them around and be able to save the board then anyone with access can
 * open it but it's saved to the tenants profile").
 *
 * A board is an ordered list of widgets on a four-column grid. Each widget shows one queue, or
 * every queue at once (`queue` null), and spans one to four columns. The portal draws them
 * (features/live/widgets.tsx); this is only what is saved.
 */

export const WALLBOARD_WIDGETS = {
  heading:          { label: 'Heading',               group: 'Board',  span: 4, rows: 1, queue: false },
  clock:            { label: 'Clock',                 group: 'Board',  span: 1, rows: 1, queue: false },
  waiting:          { label: 'Waiting now',           group: 'Now',    span: 1, rows: 1, queue: true },
  longest_wait:     { label: 'Longest wait now',      group: 'Now',    span: 1, rows: 1, queue: true },
  agents_ready:     { label: 'Agents ready',          group: 'Now',    span: 1, rows: 1, queue: true },
  agents_on_call:   { label: 'Agents on a call',      group: 'Now',    span: 1, rows: 1, queue: true },
  agents_wrap_up:   { label: 'Agents in wrap-up',     group: 'Now',    span: 1, rows: 1, queue: true },
  agents_paused:    { label: 'Agents paused',         group: 'Now',    span: 1, rows: 1, queue: true },
  calls_today:      { label: 'Calls today',           group: 'Today',  span: 1, rows: 1, queue: true },
  answered:         { label: 'Answered',              group: 'Today',  span: 1, rows: 1, queue: true },
  abandoned:        { label: 'Abandoned',             group: 'Today',  span: 1, rows: 1, queue: true },
  service_level:    { label: 'Service level',         group: 'Today',  span: 1, rows: 1, queue: true },
  avg_wait:         { label: 'Average wait',          group: 'Today',  span: 1, rows: 1, queue: true },
  longest_wait_today: { label: 'Longest wait today',  group: 'Today',  span: 1, rows: 1, queue: true },
  avg_talk:         { label: 'Average talk time',     group: 'Today',  span: 1, rows: 1, queue: true },
  talk_time:        { label: 'Talk time today',       group: 'Today',  span: 1, rows: 1, queue: true },
  agents_table:     { label: 'Agents table',          group: 'Lists',  span: 2, rows: 3, queue: true },
  agents_by_state:  { label: 'Agents by state',       group: 'Lists',  span: 4, rows: 2, queue: true },
  queue_tiles:      { label: 'Queue tiles',           group: 'Lists',  span: 4, rows: 2, queue: false },
  hourly:           { label: 'Calls by hour',         group: 'Charts', span: 2, rows: 2, queue: true },
  // The dialler (owner, 26 September 2026: answering-machine figures on the board). For one
  // campaign, or every campaign, rather than a queue.
  dial_calls:       { label: 'Campaign calls today',  group: 'Dialler', span: 1, rows: 1, queue: false },
  amd_humans:       { label: 'People reached',        group: 'Dialler', span: 1, rows: 1, queue: false },
  amd_machines:     { label: 'Machines detected',     group: 'Dialler', span: 1, rows: 1, queue: false },
  amd_screens:      { label: 'Call screens',          group: 'Dialler', span: 1, rows: 1, queue: false },
  amd_ai_checks:    { label: 'AI checks',             group: 'Dialler', span: 1, rows: 1, queue: false },
} as const;
/** The widgets that show a campaign (or every campaign) instead of a queue. */
export const CAMPAIGN_WIDGETS: ReadonlySet<string> = new Set(['dial_calls', 'amd_humans', 'amd_machines', 'amd_screens', 'amd_ai_checks']);
export type WallboardWidgetType = keyof typeof WALLBOARD_WIDGETS;
const WIDGET_TYPES = Object.keys(WALLBOARD_WIDGETS) as [WallboardWidgetType, ...WallboardWidgetType[]];

export const WallboardWidgetSchema = z.object({
  /** Stable within the board, so a drag keeps track of what moved. */
  id: z.string().min(1).max(40),
  type: z.enum(WIDGET_TYPES),
  /** The queue's key (arcq_…@domain); null for every queue at once. */
  queue: z.string().max(200).nullable().default(null),
  /** A dialler widget's campaign; null for every campaign. */
  campaign: z.string().uuid().nullable().default(null),
  span: z.number().int().min(1).max(4).default(1),
  /** Height in rows of the grid, one to four (owner, 26 September 2026: "change the size of specific tiles"). */
  rows: z.number().int().min(1).max(4).default(1),
  /** A heading's words, or a widget's own title in place of the default. */
  title: z.string().max(80).optional(),
}).strict();
export type WallboardWidget = z.infer<typeof WallboardWidgetSchema>;

export const WallboardLayoutSchema = z.object({
  widgets: z.array(WallboardWidgetSchema).max(60),
  /** Answered within this many seconds is the service level; the thresholds turn tiles amber/red. */
  sl_target_sec: z.number().int().min(1).max(600).default(20),
  wait_amber_sec: z.number().int().min(1).max(3600).default(30),
  wait_red_sec: z.number().int().min(1).max(3600).default(120),
  waiting_amber: z.number().int().min(1).max(1000).default(3),
  waiting_red: z.number().int().min(1).max(1000).default(6),
  sl_red_pct: z.number().int().min(1).max(100).default(80),
}).strict();
export type WallboardLayout = z.infer<typeof WallboardLayoutSchema>;

export const WallboardSaveSchema = z.object({
  name: z.string().trim().min(1).max(60),
  layout: WallboardLayoutSchema,
}).strict();

export interface Wallboard {
  id: string;
  name: string;
  slug: string;
  layout: WallboardLayout;
  updated_at: string;
  updated_by_name: string | null;
}
